import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ArticleCard from "@/components/ArticleCard";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import { hotWords } from "@/mocks/site";

export default function Library() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const [kw, setKw] = useState("");
  const [q, setQ] = useState("");

  const current = categories.find((c) => c.id === cat);

  const filtered = useMemo(() => {
    let list = cat === "all" ? articles : articles.filter((a) => a.cat === cat);
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(s) ||
          a.excerpt.toLowerCase().includes(s) ||
          a.catName.includes(s) ||
          a.tag.includes(s)
      );
    }
    return list;
  }, [cat, q]);

  const selectCat = (id: string) => {
    if (id === "all") params.delete("cat");
    else params.set("cat", id);
    setParams(params, { replace: true });
    setQ("");
    setKw("");
  };

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQ(kw.trim());
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16 md:pt-[72px]">
        {/* page head */}
        <section className="bg-primary-950">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <nav className="text-xs text-background-100/60" aria-label="面包屑">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="hover:text-background-50">
                    首页
                  </Link>
                </li>
                <li className="text-background-100/40">/</li>
                <li aria-current="page" className="text-background-50">
                  资料库
                </li>
                {current && (
                  <>
                    <li className="text-background-100/40">/</li>
                    <li className="text-accent-300">{current.name}</li>
                  </>
                )}
              </ol>
            </nav>
            <h1 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-4xl">
              {current ? current.name : "全部瓜料资料库"}
            </h1>
            <p className="mt-2 max-w-xl text-sm leading-6 text-background-100/70 md:text-base">
              {current
                ? current.desc
                : "集中查看影视、综艺、明星、音乐与热点话题的完整盘点内容，支持按分类浏览与关键词搜索。"}
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <div className="flex flex-wrap gap-2">
                <CatBtn active={cat === "all"} onClick={() => selectCat("all")}>
                  全部
                </CatBtn>
                {categories.map((c) => (
                  <CatBtn key={c.id} active={cat === c.id} onClick={() => selectCat(c.id)}>
                    {c.name}
                  </CatBtn>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* search + result */}
        <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <div className="flex flex-col gap-4 border-b border-background-200 pb-5 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-foreground-600">
              共整理 <span className="font-bold text-primary-700">{filtered.length}</span> 篇相关盘点内容
            </p>
            <form onSubmit={onSearch} className="flex w-full items-center gap-2 sm:w-auto">
              <div className="relative flex-1 sm:w-72">
                <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  value={kw}
                  onChange={(e) => setKw(e.target.value)}
                  placeholder="搜索关键词，如：演唱会 / 悬疑剧…"
                  className="h-11 w-full rounded-xl border border-background-200 bg-background-100 pr-4 pl-10 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-300"
                />
              </div>
              <button
                type="submit"
                className="flex h-11 items-center justify-center whitespace-nowrap rounded-xl bg-primary-500 px-5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>
          </div>

          {q && (
            <p className="mt-4 text-sm text-foreground-600">
              关键词“<span className="font-semibold text-primary-700">{q}</span>”的搜索结果：
            </p>
          )}

          {filtered.length === 0 ? (
            <div className="mt-10 flex flex-col items-center justify-center rounded-2xl border border-dashed border-background-300 bg-background-100 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-background-50 text-foreground-300">
                <i className="ri-inbox-2-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-800">没有找到相关内容</p>
              <p className="mt-1 text-sm text-foreground-500">试试换个分类或关键词吧。</p>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((a) => (
                <ArticleCard key={a.id} a={a} />
              ))}
            </div>
          )}
        </section>

        {/* quick tags */}
        <section className="bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
            <div className="flex items-center gap-2">
              <i className="ri-fire-line text-accent-500"></i>
              <h2 className="font-heading text-base font-bold text-foreground-900">大家都在搜</h2>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {hotWords.map((w) => (
                <button
                  key={w}
                  onClick={() => {
                    setKw(w);
                    setQ(w);
                  }}
                  className="whitespace-nowrap rounded-full bg-background-50 px-4 py-2 text-sm text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-800"
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

function CatBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
        active
          ? "bg-accent-500 text-foreground-950"
          : "bg-background-50/10 text-background-100 hover:bg-background-50/20"
      }`}
    >
      {children}
    </button>
  );
}